import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Layout,
  Select,
  Banner,
  InlineStack,
  BlockStack,
  Text,
  DatePicker,
} from '@shopify/polaris';
import { availabilityAPI } from '../services/api';

const RentalCalendarComponent = ({ productId, productData, onDateSelect, onAvailabilityCheck, onQuantityChange }) => {
  const [selectedDates, setSelectedDates] = useState(undefined);
  const [quantity, setQuantity] = useState('1');
  const [availabilityData, setAvailabilityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [datePickerState, setDatePickerState] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });

  // Helper function to safely format currency
  const formatCurrency = (value) => {
    const numValue = Number(value);
    return isNaN(numValue) ? '0.00' : numValue.toFixed(2);
  };

  const handleMonthChange = useCallback((month, year) => {
    setDatePickerState({ month, year });
  }, []);

  const checkAvailability = useCallback(async () => {
    console.log('Checking availability for:', { selectedDates, productId, quantity });
    if (!selectedDates || !selectedDates.start || !selectedDates.end || !productId || selectedDates.start >= selectedDates.end) {
      setError('Please select start and end dates and make sure the start date is before the end date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await availabilityAPI.check({
        productId,
        startDate: selectedDates.start,
        endDate: selectedDates.end,
        quantity: parseInt(quantity),
      });

      setAvailabilityData(response.data);
      
      if (onAvailabilityCheck) {
        onAvailabilityCheck(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to check availability');
      setAvailabilityData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedDates, productId, quantity, onAvailabilityCheck]);

  // Auto-check availability when dates or quantity change
  useEffect(() => {
    if (selectedDates && selectedDates.start && selectedDates.end && productId && selectedDates.start < selectedDates.end) {
      checkAvailability();
    }
  }, [selectedDates, productId, checkAvailability]);

  // Notify parent of quantity changes
  useEffect(() => {
    if (onQuantityChange) {
      onQuantityChange(quantity);
    }
  }, [quantity, onQuantityChange]);

  const handleDateChange = useCallback((dates) => {
    setSelectedDates(dates);
    setAvailabilityData(null); // Reset availability data when dates change

    if (onDateSelect) {
      onDateSelect(dates);
    }
  }, [onDateSelect]);



  const calculateRentalDays = () => {
    if (!selectedDates || !selectedDates.start || !selectedDates.end) return 0;
    const timeDiff = selectedDates.end.getTime() - selectedDates.start.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
  };

  const quantityOptions = Array.from({ length: 10 }, (_, i) => ({
    label: `${i + 1}`,
    value: `${i + 1}`,
  }));

  return (
    <Layout>
      <Layout.Section>
        <Card sectioned>
          <BlockStack gap="400">
            {error && (
              <Banner status="critical" onDismiss={() => setError('')}>
                {error}
              </Banner>
            )}

            {/* Polaris DatePicker for Range Selection */}
            <BlockStack gap="400">
              <Text variant="bodyMd" tone="subdued">
                Click to select start date, then click to select end date
              </Text>
              
              {/* Month/Year Display */}
              <InlineStack align="center" gap="200">
                <Text variant="bodyMd" fontWeight="semibold">
                  {new Date(datePickerState.year, datePickerState.month).toLocaleDateString('en-US', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </Text>
              </InlineStack>
              
              <DatePicker
                month={datePickerState.month}
                year={datePickerState.year}
                selected={selectedDates}
                allowRange={true}
                disableDatesBefore={new Date()}
                onMonthChange={handleMonthChange}
                onChange={handleDateChange}
              />
            </BlockStack>

            <Layout>
              <Layout.Section oneThird>
                <Select
                  label="Quantity"
                  options={quantityOptions}
                  value={quantity}
                  onChange={setQuantity}
                />
              </Layout.Section>
              
              <Layout.Section oneThird>
                <BlockStack gap="100">
                  <Text variant="bodyMd" tone="subdued">Rental Days</Text>
                  <Text variant="bodyMd" fontWeight="bold">{calculateRentalDays()}</Text>
                </BlockStack>
              </Layout.Section>

              <Layout.Section oneThird>
                <BlockStack gap="100">
                  <div style={{ marginTop: '1.5rem' }}>
                    {loading && (
                      <Text variant="bodyMd" tone="subdued">
                        Checking availability...
                      </Text>
                    )}
                  </div>
                </BlockStack>
              </Layout.Section>
            </Layout>
          </BlockStack>
        </Card>
      </Layout.Section>

      {availabilityData && (
        <Layout.Section>
          <Card sectioned>
            <BlockStack gap="400">
              <Text variant="headingMd">Availability Results</Text>
              
              {availabilityData.available ? (
                <Banner status="success">
                  <InlineStack>
                    <div>
                      Available! {availabilityData.availableQuantity} units available for your requested dates.
                    </div>
                  </InlineStack>
                </Banner>
              ) : (
                <Banner status="critical">
                  Not available. Only {availabilityData.availableQuantity} units available, but you requested {availabilityData.requestedQuantity}.
                </Banner>
              )}

              {availabilityData.pricing && (
                <InlineStack>
                  <div>
                    <Text variant="bodyMd" fontWeight="bold">Pricing Details:</Text>
                    <ul>
                      <li>Rental Days: {availabilityData.rentalDays}</li>
                      <li>Subtotal: ${formatCurrency(availabilityData.pricing.subtotal)}</li>
                      {Number(availabilityData.pricing.securityDeposit || 0) > 0 && (
                        <li>Security Deposit: ${formatCurrency(availabilityData.pricing.securityDeposit)}</li>
                      )}
                      <li><strong>Total: ${formatCurrency(availabilityData.pricing.total)}</strong></li>
                    </ul>
                  </div>
                </InlineStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      )}

      {/* Product Information */}
      {productData && (
        <Layout.Section>
          <Card sectioned>
            <BlockStack gap="400">
              <Text variant="headingMd">Product Information</Text>
              <InlineStack gap="400">
                <div>
                  <Text variant="bodyMd" tone="subdued">Daily Rate:</Text>
                  <Text variant="bodyMd" fontWeight="bold">
                    ${productData.dailyRate}
                  </Text>
                </div>
                <div>
                  <Text variant="bodyMd" tone="subdued">Total Inventory:</Text>
                  <Text variant="bodyMd" fontWeight="bold">
                    {productData.totalQuantity}
                  </Text>
                </div>
                <div>
                  <Text variant="bodyMd" tone="subdued">Min. Rental:</Text>
                  <Text variant="bodyMd" fontWeight="bold">
                    {productData.minimumRentalDays} days
                  </Text>
                </div>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      )}
    </Layout>
  );
};

export default RentalCalendarComponent;
